/**
 * Site-wide banner: shown when there is an active support-access grant
 * touching the current user.
 *
 *  - org_admin / security_analyst / viewer (tenant user): sees the banner when
 *    a support engineer holds an active grant into THEIR tenant.
 *  - support_engineer: sees the banner whenever they hold their own active
 *    grant (regardless of which tenant).
 *  - super_admin / auditor: not shown — they have aggregate views for this.
 *
 * The banner polls every 30s. It is the customer's at-a-glance signal that
 * "someone from IdentityCentral can currently read your tenant data."
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldAlert, Clock } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { isPlatformRole } from '@/lib/permissions';
import {
  listAdmin,
  listOrg,
  type SupportAccessRequest,
} from '@/lib/api/supportAccess';

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'expiring…';
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m left`;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m left`;
}

export function ActiveSupportBanner() {
  const user = useAuthStore((s) => s.user);
  const [active, setActive] = useState<SupportAccessRequest | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick every minute so the countdown updates without a full refetch.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Audience filter: only support_engineer + tenant users see this banner.
  // super_admin / auditor have the platform support-access page instead.
  const shouldShow = useMemo(() => {
    if (!user) return false;
    if (user.role === 'support_engineer') return true;
    return !isPlatformRole(user.role);
  }, [user]);

  useEffect(() => {
    if (!shouldShow) return;
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const res =
          user?.role === 'support_engineer'
            ? await listAdmin('active')
            : await listOrg('active');
        if (cancelled) return;
        const first = (res.items ?? []).find((r) => r.status === 'active') ?? null;
        setActive(first);
      } catch {
        // Banner is best-effort; silently swallow API errors so we don't
        // spam toast / console for every page mount in dev.
      }
    };
    void fetchActive();
    const poll = setInterval(fetchActive, 30_000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [shouldShow, user?.role]);

  if (!shouldShow || !active) return null;

  const remaining = active.expires_at ? new Date(active.expires_at).getTime() - now : 0;
  const breakGlass = active.decision_path === 'break_glass';
  const isSupportEngineer = user?.role === 'support_engineer';

  const linkTo = isSupportEngineer ? '/admin/support-access' : '/org/settings/support-access';

  return (
    <div
      className={
        breakGlass
          ? 'border-b border-threat-500/40 bg-threat-500/10'
          : 'border-b border-amber-500/40 bg-amber-500/10'
      }
    >
      <div className="px-6 py-2.5 flex items-center gap-3 text-sm">
        {breakGlass ? (
          <ShieldAlert className="h-4 w-4 text-threat-400 shrink-0" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {isSupportEngineer ? (
            <span className="text-amber-100">
              You hold an active support grant on{' '}
              <span className="font-semibold">{active.org_id}</span>. Every action is logged in the tenant&apos;s audit trail.
            </span>
          ) : (
            <span className={breakGlass ? 'text-threat-100' : 'text-amber-100'}>
              {breakGlass ? (
                <>
                  <span className="font-semibold">BREAK-GLASS:</span>{' '}
                  IdentityCentral support engineer{' '}
                  <span className="font-mono">{active.actor_email ?? active.actor_user_id}</span>{' '}
                  has time-boxed read access to your tenant.
                </>
              ) : (
                <>
                  Support engineer{' '}
                  <span className="font-mono">{active.actor_email ?? active.actor_user_id}</span>{' '}
                  has active read access to your tenant (you approved this).
                </>
              )}
            </span>
          )}
        </div>
        <span className="hidden md:inline-flex items-center gap-1 text-xs text-dark-300">
          <Clock className="h-3 w-3" />
          {formatRemaining(remaining)}
        </span>
        <Link
          to={linkTo}
          className="text-xs font-medium underline underline-offset-2 text-white/90 hover:text-white shrink-0"
        >
          {isSupportEngineer ? 'View grant' : 'Manage'}
        </Link>
      </div>
    </div>
  );
}
